import { createFileRoute } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Check, ShoppingBag, Wallet, X } from "lucide-react";
import { toast } from "sonner";
import { AdminPageHeader } from "@/components/admin/admin-shell";
import { adjustBalance } from "@/lib/admin";
import { azError } from "@/lib/az-errors";
import { ORDER_STATUS_LABEL,
  DELIVERY_STATUS_LABEL,
  updateDelivery, fetchAllOrders, updateOrderStatus } from "@/lib/orders";
import { supabase } from "@/integrations/supabase/client";

export const Route = createFileRoute("/_authenticated/admin/sifarisler")({
  component: Page,
});

function Page() {
  const qc = useQueryClient();
  const { data, isLoading } = useQuery({ queryKey: ["admin-orders"], queryFn: fetchAllOrders });

  const rows = data ?? [];
  const pending = rows.filter((r) => r.status !== "paid" && r.status !== "rejected");
  const paidSum = rows
    .filter((r) => r.status === "paid")
    .reduce((s, r) => s + Number(r.total || 0), 0);

  const confirmPay = useMutation({
    mutationFn: async (order: (typeof rows)[number]) => {
      if (!order.owner_id) throw new Error("Bu sifarişin mağaza sahibi təyin edilməyib");
      await adjustBalance(
        order.owner_id,
        Number(order.total || 0),
        `Sifariş ödənişi: ${order.product}`,
        "order",
      );
      await updateOrderStatus(order.id, "paid");
      await supabase.from("notifications").insert({
        user_id: order.owner_id,
        title: "Sifariş ödənişi təsdiqləndi",
        body: `${order.product} sifarişi üzrə ${Number(order.total).toFixed(2)} AZN balansınıza əlavə edildi.`,
        kind: "system",
        link: "/balans",
      });
    },
    onSuccess: () => {
      toast.success("Ödəniş təsdiqləndi və məbləğ balansa oturdu");
      void qc.invalidateQueries({ queryKey: ["admin-orders"] });
    },
    onError: (e) => toast.error(azError(e)),
  });

  const setDelivery = useMutation({
    mutationFn: (v: { id: string } & Record<string, string>) => {
      const { id, ...patch } = v;
      return updateDelivery(id, patch);
    },
    onSuccess: () => {
      toast.success("Çatdırılma məlumatı yeniləndi");
      qc.invalidateQueries({ queryKey: ["admin-orders"] });
    },
    onError: (e) => toast.error(azError(e)),
  });

  const setStatus = useMutation({
    mutationFn: (v: { id: string; status: string }) => updateOrderStatus(v.id, v.status),
    onSuccess: () => {
      toast.success("Status yeniləndi");
      void qc.invalidateQueries({ queryKey: ["admin-orders"] });
    },
    onError: (e) => toast.error(azError(e)),
  });

  return (
    <div>
      <AdminPageHeader
        title="Sifarişlər"
        description="Mağaza sifarişlərini təsdiqləyin — ödəniş mağaza sahibinin balansına oturur"
      />

      <div className="grid grid-cols-3 gap-3">
        {[
          { label: "Ümumi sifariş", value: String(rows.length), icon: ShoppingBag },
          { label: "Gözləyən", value: String(pending.length), icon: ShoppingBag },
          { label: "Ödənilib (AZN)", value: paidSum.toFixed(2), icon: Wallet },
        ].map((c) => (
          <div key={c.label} className="rounded-2xl border border-border bg-card p-4 shadow-card">
            <div className="flex items-center gap-2 text-xs font-semibold text-muted-foreground">
              <c.icon className="size-4" />
              {c.label}
            </div>
            <p className="mt-2 text-xl font-extrabold">{isLoading ? "…" : c.value}</p>
          </div>
        ))}
      </div>

      <div className="mt-4 grid gap-2">
        {isLoading ? (
          <p className="text-sm text-muted-foreground">Yüklənir…</p>
        ) : rows.length === 0 ? (
          <p className="rounded-2xl border border-border bg-card p-4 text-sm text-muted-foreground">
            Hələ sifariş yoxdur.
          </p>
        ) : (
          rows.map((o) => (
            <div key={o.id} className="rounded-2xl border border-border bg-card p-3 shadow-card">
              <div className="flex flex-wrap items-start justify-between gap-2">
                <div className="min-w-0">
                  <p className="text-sm font-bold">
                    {o.product} × {o.quantity} ·{" "}
                    <span className="text-primary">{Number(o.total).toFixed(2)} AZN</span>
                  </p>
                  <p className="mt-0.5 text-xs text-muted-foreground">
                    {o.contact_phone}
                    {o.customer_name ? ` · ${o.customer_name}` : ""} ·{" "}
                    {new Date(o.created_at).toLocaleString("az-AZ", {
                      dateStyle: "short",
                      timeStyle: "short",
                    })}
                  </p>
                  {o.note && <p className="mt-1 text-xs">{o.note}</p>}
                </div>
                <span className="rounded-full bg-muted px-2 py-0.5 text-[11px] font-bold">
                  {ORDER_STATUS_LABEL[o.status] ?? o.status}
                </span>
              </div>

              <div className="mt-2 flex flex-wrap gap-1.5">
                {o.status !== "paid" && (
                  <button
                    type="button"
                    disabled={confirmPay.isPending}
                    onClick={() => confirmPay.mutate(o)}
                    className="inline-flex items-center gap-1 rounded-xl bg-primary px-3 py-1.5 text-xs font-bold text-primary-foreground disabled:opacity-60"
                  >
                    <Wallet className="size-3.5" /> Ödənişi təsdiqlə (balansa yaz)
                  </button>
                )}
                {o.status === "pending" && (
                  <button
                    type="button"
                    onClick={() => setStatus.mutate({ id: o.id, status: "confirmed" })}
                    className="inline-flex items-center gap-1 rounded-xl border border-border px-3 py-1.5 text-xs font-semibold hover:bg-accent"
                  >
                    <Check className="size-3.5" /> Sifarişi təsdiqlə
                  </button>
                )}
                {o.status !== "rejected" && (
                  <button
                    type="button"
                    onClick={() => setStatus.mutate({ id: o.id, status: "rejected" })}
                    className="inline-flex items-center gap-1 rounded-xl border border-destructive/40 px-3 py-1.5 text-xs font-semibold text-destructive hover:bg-destructive/10"
                  >
                    <X className="size-3.5" /> Rədd et
                  </button>
                )}
              </div>

              <div className="mt-2 flex flex-wrap items-center gap-2 rounded-xl border border-dashed border-border p-2">
                <span className="text-xs font-semibold text-muted-foreground">Çatdırılma:</span>
                <select
                  aria-label="Çatdırılma statusu"
                  value={o.delivery_status ?? "pending"}
                  onChange={(e) => setDelivery.mutate({ id: o.id, delivery_status: e.target.value })}
                  className="h-8 rounded-lg border border-border bg-background px-2 text-xs font-semibold"
                >
                  {Object.entries(DELIVERY_STATUS_LABEL).map(([v, l]) => (
                    <option key={v} value={v}>
                      {l}
                    </option>
                  ))}
                </select>
                <input
                  aria-label="Kuryer"
                  defaultValue={o.courier ?? ""}
                  placeholder="Kuryer"
                  onBlur={(e) => {
                    if (e.target.value !== (o.courier ?? ""))
                      setDelivery.mutate({ id: o.id, courier: e.target.value });
                  }}
                  className="h-8 w-32 rounded-lg border border-border bg-background px-2 text-xs"
                />
                <input
                  aria-label="Çatdırılma qeydi"
                  defaultValue={o.delivery_note ?? ""}
                  placeholder="Qeyd"
                  onBlur={(e) => {
                    if (e.target.value !== (o.delivery_note ?? ""))
                      setDelivery.mutate({ id: o.id, delivery_note: e.target.value });
                  }}
                  className="h-8 min-w-32 flex-1 rounded-lg border border-border bg-background px-2 text-xs"
                />
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
}
